import { getHint, SudokuStrategy } from "sudokuru";
import {
  CellProps,
  ClassicObjectProps,
  HintStrategy,
} from "../../../../Functions/LocalDatabase";
import { getWrongValueDemoCase } from "../../../../Data/hints/demo_wrong_value_hints";
import { HintProps } from "../../SudokuBoard";
import { generateBoxIndex } from "./CellFunctions";
import {
  getWrongValueDemoCaseId,
  isWrongValueDemoDifficulty,
} from "./DifficultyFunctions";

/**
 * Retrieves a hint for the current sudoku puzzle state based on the specified strategy order.
 * The hint is generated using the current puzzle state, solution, and an strategy array that determines priority order of hint.
 * Hint information is then processed through the hintInjections function to further customize the output.
 * @param puzzle - current sudoku puzzle state
 * @param solution - sudoku solution
 * @param strategies - order of strategies to use for generating the hint
 * @returns Hint object containing the hint information
 */
export const getSudokuHint = (
  puzzle: CellProps[][],
  strategies: SudokuStrategy[],
  solution?: number[][],
): HintProps => {
  const puzzleState = convertPuzzleStateToSudokuruFormat(puzzle);

  let hint: HintProps;
  if (solution) {
    const puzzleSolution = convertPuzzleSolutionToSudokuruFormat(solution);
    hint = getHint(
      puzzleState.puzzleValues,
      puzzleState.puzzleNotes,
      strategies,
      puzzleSolution,
    ) as unknown as HintProps;
  } else {
    hint = getHint(
      puzzleState.puzzleValues,
      puzzleState.puzzleNotes,
      strategies,
    ) as unknown as HintProps;
  }
  hint = hintInjections(hint);
  return hint;
};

/**
 * This function takes in the hint object and overrides / injects values
 * The Frontend uses groups to indicate regions of interest of a hint, whereas sudokuru package uses groups
 * to store what groups cause a strategy to occur. OBVIOUS_SINGLE is not caused by any groups, but Frontend wants to highlight the box
 * @param hint Hint object returned from sudokuru package
 * @returns Updated Hint object
 */
export const hintInjections = (hint: HintProps) => {
  if (hint.strategy === "OBVIOUS_SINGLE") {
    hint.groups.push([2, generateBoxIndex(hint.cause[0][0], hint.cause[0][1])]);
  }
  return hint;
};

/**
 * Conversion function to be used for retrieving hints from Sudokuru package.
 * Sudokuru package uses string[][] for solution, while Frontend uses number[][]
 * @param puzzleSolution Sudoku puzzle solution in Frontend format
 * @returns Sudoku puzzle solution in Sudokuru package format
 */
const convertPuzzleSolutionToSudokuruFormat = (
  puzzleSolution: number[][],
): string[][] => {
  const convertedPuzzleSolution: string[][] = [];
  puzzleSolution.forEach(function (value, index) {
    convertedPuzzleSolution.push([]);
    convertedPuzzleSolution[index] = value.map(String);
  });
  return convertedPuzzleSolution;
};

/**
 * Conversion function to be used for retrieving hints from Sudokuru package.
 * Sudokuru package uses two string[][] objects for representing notes and values.
 * @param puzzle Sudoku puzzle notes and values in Frontend format
 * @returns
 */
const convertPuzzleStateToSudokuruFormat = (puzzle: CellProps[][]) => {
  const convertedPuzzleValues: string[][] = [];
  const convertedPuzzleNotes: string[][] = [];
  for (let r = 0; r < puzzle.length; r++) {
    convertedPuzzleValues.push([]);
    for (let c = 0; c < puzzle[r].length; c++) {
      convertedPuzzleNotes.push([]);
      let value: string = "0";
      if (puzzle[r][c].type === "note") {
        const entry = puzzle[r][c].entry as number[];
        for (const note of entry) {
          convertedPuzzleNotes[c + r * puzzle.length].push(note.toString());
        }
      } else {
        const entry = puzzle[r][c].entry as number;
        value = entry.toString();
      }

      convertedPuzzleValues[r].push(value);
    }
  }
  return {
    puzzleValues: convertedPuzzleValues,
    puzzleNotes: convertedPuzzleNotes,
  };
};

/**
 * Retrieves a hint for the current sudoku puzzle board and updates the board's hint statistics.
 * The hint is generated using the current puzzle state, solution, and a strategy array that determines
 * the priority order of hint strategies. Per-strategy hint counts are incremented to track which
 * strategies have been used throughout the game.
 * @param sudokuBoard - current sudoku board state including puzzle state and statistics
 * @param strategyArray - order of strategies to use for generating the hint
 * @returns Object containing the hint information and the updated board with incremented statistics
 */
export const getSudokuBoardHint = (
  sudokuBoard: ClassicObjectProps,
  strategyArray: SudokuStrategy[],
) => {
  if (isWrongValueDemoDifficulty(sudokuBoard.statistics.difficulty)) {
    const demoCase = getWrongValueDemoCase(
      getWrongValueDemoCaseId(sudokuBoard.statistics.difficulty),
    );
    sudokuBoard.statistics.numHintsUsed++;

    const existingWrongValueStats =
      sudokuBoard.statistics.numHintsUsedPerStrategy.find(
        (strategy) => strategy.hintStrategy === "WRONG_VALUE",
      );
    if (existingWrongValueStats) {
      existingWrongValueStats.numHintsUsed++;
    } else {
      sudokuBoard.statistics.numHintsUsedPerStrategy.push({
        hintStrategy: "WRONG_VALUE",
        numHintsUsed: 1,
      });
    }

    return {
      hint: demoCase.hint as HintProps,
      updatedBoard: sudokuBoard,
    };
  }

  strategyArray.unshift("SIMPLIFY_NOTES");
  strategyArray.unshift("AMEND_NOTES");

  const hint = getSudokuHint(
    sudokuBoard.puzzleState,
    strategyArray,
    sudokuBoard.puzzleSolution,
  );
  sudokuBoard.statistics.numHintsUsed++;

  // increment per-strategy hints
  let incrementedStrategy = false;
  for (const [
    i,
    strategies,
  ] of sudokuBoard.statistics.numHintsUsedPerStrategy.entries()) {
    if (strategies.hintStrategy === hint.strategy) {
      sudokuBoard.statistics.numHintsUsedPerStrategy[i].numHintsUsed++;
      incrementedStrategy = true;
      break;
    }
  }
  if (!incrementedStrategy) {
    sudokuBoard.statistics.numHintsUsedPerStrategy.push({
      hintStrategy: hint.strategy,
      numHintsUsed: 1,
    } as { hintStrategy: HintStrategy; numHintsUsed: number });
  }

  return { hint, updatedBoard: sudokuBoard };
};
